import { filterClasses } from "@swivl-mono/helpers";
import React from "react";
import Markdown, { defaultUrlTransform } from "react-markdown";
import remarkGfm from "remark-gfm";

import { formatTime } from "../Helpers/formatTime";
import { remarkPhoneLinks } from "../Markdown/remarkPhoneLinks";
import { ChatMessage } from "../Types/BotChat.Types";
import { DeliveryTickState, DeliveryTicks, outboundTickState } from "./DeliveryTicks";

interface MessageBubbleProps {
  message: ChatMessage;
  /** Optional override for the speaker label on bot messages — defaults to "Bot". */
  botLabel?: string;
  /**
   * Whose outbound messages carry the delivery/seen ticks. Each side sees ticks
   * on its OWN messages (does my message reach the other party?):
   *   - "user" (the widget): ticks on the user's own messages — delivered =
   *     the bot/server got it, seen = the recipient (bot, or agent once taken
   *     over) read it. Bot messages carry no tick (the user is reading them).
   *   - "agent" (CC V3 / AI Studio, default): ticks on bot/agent outbound
   *     messages — delivered + seen *by the user* (delivery.seenBy.user).
   */
  perspective?: "user" | "agent";
  /**
   * Which role counts as "me" — right-aligned, in the brand colour.
   *
   * Defaults to "user", which is correct on the customer's own screen: the
   * viewer IS the user. The agent console passes "assistant", because there
   * the viewer is the agent and their messages are stored with role
   * "assistant" (the same bucket as the bot — both are our side of the
   * conversation). Without this the agent console renders the thread from
   * the customer's point of view, with the customer on the right.
   *
   * Deliberately separate from `perspective` (which only picks whose
   * messages carry delivery ticks): their correct defaults differ, so
   * folding them together would change tick behaviour on surfaces that
   * only want the alignment left alone.
   */
  ownRole?: "user" | "assistant";
  /**
   * Bubble colouring.
   *   - "widget" (default): own message solid in the primary colour with white
   *     text — the customer-facing look. THIS IS SHIPPED TO CUSTOMERS via
   *     ChatWindow → MessageList; do not change it for a console-only reason.
   *   - "console": v2's agent-console palette (`Bubble.scss`) — own in grey-50,
   *     the other party in primary-50, black text on both.
   */
  palette?: "widget" | "console";
  /**
   * Overrides the footer's time. The voice transcript passes the wall-clock
   * time with the time into the call in brackets ("3:42 PM (0:06)") — a call
   * is read by elapsed time, but that cannot be the only thing shown or a
   * closed call has no absolute reference at all (PROD-774). Omit for the
   * plain clock time derived from `createdAt`.
   */
  timeLabel?: string;
}

/**
 * Agent perspective — tick the bot/agent (assistant) outbound message by how
 * far it got *to the user*: seenBy.user → seen; deliveryStatus
 * "delivered" → delivered; "sending"/"sent"/etc → sent. Null for the user's
 * own messages AND for assistant messages with no delivery data surfaced yet
 * (so the agent timeline shows no tick until the data path delivers it).
 */
function agentTickState(message: ChatMessage): DeliveryTickState | null {
  if (message.role !== "assistant") return null;
  // Shared with the voice transcript so every surface reads a status the same
  // way — including `undelivered` (cut short) and `failed`, which used to be
  // flattened into a plain "sent" tick and read as success.
  return outboundTickState(message.deliveryStatus, !!message.seenBy?.user);
}

/**
 * User perspective — tick the user's own (user) outbound message by how
 * far it got *to the recipient* (the bot, or the agent once taken over). The
 * widget advances `deliveryStatus` locally ("sent" → "delivered" → "seen") as
 * the server acks the turn / the recipient responds; `seenBy.members` (a human
 * agent opened it) also counts as seen. Null for the bot's messages.
 */
function userTickState(message: ChatMessage): DeliveryTickState | null {
  if (message.role !== "user") return null;
  if (message.deliveryStatus === "seen" || message.seenBy?.members) return "seen";
  if (message.deliveryStatus === "delivered") return "delivered";
  return "sent";
}

/**
 * Single chat-message bubble — Figma node 201:11782:
 *   - **Bot:** bubble in grey-50 with sharp bottom-left corner (left-aligned),
 *     "Bot • 2:00 PM" footer *below* the bubble (mono, grey-500).
 *   - **User:** bubble in primary with sharp bottom-right corner, "2:01 PM"
 *     footer *below* the bubble, right-aligned.
 *
 * The delivery/seen tick sits in the *sender's own* footer — see `perspective`.
 */
export function MessageBubble({
  message,
  botLabel = "Bot",
  perspective = "agent",
  ownRole = "user",
  palette = "widget",
  timeLabel
}: MessageBubbleProps): React.ReactElement {
  const isUser = message.role === "user";
  // Side + colour follow "is this mine", which is not the same question as
  // "who said it" — the footer label below still keys off `isUser`.
  const isOwn = ownRole === "assistant" ? !isUser : isUser;
  const time = timeLabel ?? (message.createdAt ? formatTime(message.createdAt) : "");
  const tickState = perspective === "user" ? userTickState(message) : agentTickState(message);
  // The Outbound Gate's answer is spelled out, not only iconed: "simulated (flag off)" / "blocked (opted out)".
  const gateNote =
    tickState === "simulated" || tickState === "blocked"
      ? `${tickState}${message.deliveryReason ? ` (${message.deliveryReason.replace(/_/g, " ")})` : ""}`
      : null;
  // v2 tints the *other* party's bubble and leaves your own neutral — the
  // reverse of the widget, where your own message carries the brand colour.
  // Console footers sit alongside the grey-600 debug rows in the timeline, so
  // they match them. The widget keeps grey-500, its designed value.
  const footerClasses = filterClasses(
    "flex items-center gap-1 font-mono text-xs leading-4",
    palette === "console" ? "text-grey-600" : "text-grey-500"
  );
  const bubbleTone =
    palette === "console"
      ? isOwn
        ? "bg-grey-50 text-grey-900"
        : "bg-primary-50 text-grey-900"
      : isOwn
        ? "bg-primary-500 text-white"
        : "bg-grey-50 text-grey-900";
  // Agent-authored messages are labeled with the agent's NAME — "Bot" for a
  // human's words misattributes the transcript (ENG-2957 drill, 2026-08-16).
  // Authorship rides `author`/`agentName` on the canonical shape; fetched rows
  // carry it in metadata (isAgentMessage/agentName) — accept either.
  const metadata = message.metadata as { isAgentMessage?: boolean; agentName?: string } | undefined;
  const isAgentAuthored = message.author === "agent" || metadata?.isAgentMessage === true;
  const speakerLabel = isAgentAuthored ? (message.agentName ?? metadata?.agentName ?? "Agent") : botLabel;

  return (
    <div className={filterClasses("mb-3 flex flex-col gap-1", isOwn ? "items-end" : "items-start")}>
      <div
        className={filterClasses(
          // Console caps at 600px as well as 85%: the agent panel is far wider
          // than a phone-sized widget, and an unbroken line most of the way
          // across it is hard to read. The widget keeps the percentage alone.
          palette === "console" ? "max-w-[min(85%,600px)]" : "max-w-[85%]",
          "rounded-lg px-3 py-2.5 text-base leading-snug break-words",
          isOwn ? "rounded-br-none" : "rounded-bl-none",
          bubbleTone
        )}
      >
        <Markdown
          // GFM's autolink literals turn bare URLs and emails into links —
          // the model often emits them unbracketed (ENG-2957, 2026-08-17).
          // Phones get the same treatment from remarkPhoneLinks, on the parsed
          // tree so an explicit markdown link is never corrupted (ENG-3042).
          remarkPlugins={[remarkGfm, remarkPhoneLinks]}
          // The default transform strips tel: (its whitelist is http/https/
          // mailto/…), leaving our phone links with an empty href.
          urlTransform={(url) => (url.startsWith("tel:") ? url : defaultUrlTransform(url))}
          components={{
            a: ({ href, children }) => (
              <a href={href} target="_blank" rel="noopener noreferrer" className="underline">
                {children}
              </a>
            ),
            p: ({ children }) => <p className="mb-1 last:mb-0">{children}</p>,
            ul: ({ children }) => <ul className="mb-1 ml-4 list-disc last:mb-0">{children}</ul>,
            ol: ({ children }) => <ol className="mb-1 ml-4 list-decimal last:mb-0">{children}</ol>,
            li: ({ children }) => <li className="mb-0.5">{children}</li>
          }}
        >
          {message.content}
        </Markdown>
      </div>
      {isUser
        ? time && (
            <span className={footerClasses}>
              {time}
              {tickState && <DeliveryTicks state={tickState} />}
            </span>
          )
        : time && (
            <span className={footerClasses}>
              {`${speakerLabel} • ${time}`}
              {tickState && <DeliveryTicks state={tickState} title={gateNote ?? undefined} />}
              {gateNote && <span className={tickState === "blocked" ? "text-red-500" : "text-purple-600"}>{gateNote}</span>}
            </span>
          )}
    </div>
  );
}
